import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  Compass, Tent, Users, ShieldCheck, Heart, MapPin, Sparkles,
  Calendar, Smartphone, Apple,
} from "lucide-react";
import { SOCIAL } from "../lib/social";
import { api } from "../lib/api";
import { withUtm, PRINT_UTM } from "../lib/utm";

/**
 * /print/poster — A4 portrait printable poster.
 *
 * Designed to be printed at home (A4) or scaled up to A3 for outdoor shows
 * and campsite reception desks. Print CSS hides the entire app shell and
 * renders only this canvas at exact A4 dimensions (210mm x 297mm).
 *
 * Brand voice: Swiss editorial, high-contrast, lots of negative space.
 * No emoji icons — all lucide-react SVGs so the print stays crisp.
 *
 * Workflow: visit /print/poster on desktop → File → Print → Save as PDF
 * → upload to a printer or A3 home printer. Done.
 *
 * Live stats: fetches the real campsite count from /api/campsites/stats
 * on mount so the printed poster always reflects the current catalogue.
 */
const PUBLIC_URL =
  (process.env.REACT_APP_PUBLIC_URL || "").trim().replace(/\/$/, "") ||
  "https://camp-compass.uk";

/**
 * QR target URL — UTM-tagged so poster scans are attributed in the
 * admin signup-source dashboard via the existing
 * `lib/signupSource.js` capture pipeline.
 */
const QR_URL = withUtm(PUBLIC_URL, PRINT_UTM.poster);

// Public launch date — visible on every poster so visitors know when
// the app goes truly live. Update this single constant if the date moves.
const LAUNCH_DATE = new Date("2026-05-31T00:00:00Z");
const LAUNCH_LABEL = LAUNCH_DATE.toLocaleDateString("en-GB", {
  day: "numeric", month: "long", year: "numeric",
});

export default function Poster() {
  const [count, setCount] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/campsites/stats").then(({ data }) => {
      if (alive && typeof data?.total === "number") setCount(data.total);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const countLabel = count != null ? count.toLocaleString() : "11,000+";
  const countFeat = count != null ? `${count.toLocaleString()} UK sites` : "11,000+ UK sites";

  return (
    <div className="bg-[#fafafa] min-h-screen print:bg-white" data-testid="poster-page">
      {/* Print-only stylesheet: hide everything but the canvas, A4 page,
          remove default margins so the poster bleeds nicely. */}
      <style>{`
        @page { size: A4; margin: 0; }
        @media print {
          html, body { background: #fff !important; }
          .print-hide { display: none !important; }
          .print-canvas {
            box-shadow: none !important;
            border: none !important;
            margin: 0 !important;
            page-break-after: always;
          }
        }
      `}</style>

      {/* Toolbar (screen only) */}
      <div className="print-hide max-w-[820px] mx-auto px-6 py-6 flex items-center justify-between">
        <div>
          <div className="eyebrow text-[#ff4500]">Print pack</div>
          <h1 className="font-display text-2xl">Launch poster · A4</h1>
          <p className="text-sm text-[#666] mt-1">
            Press <kbd className="px-1.5 py-0.5 bg-white border hairline mono text-[11px]">Ctrl+P</kbd>{" "}
            (or <kbd className="px-1.5 py-0.5 bg-white border hairline mono text-[11px]">⌘+P</kbd>)
            and pick <strong>Save as PDF</strong>. Margins: <strong>None</strong>. Scale: <strong>100%</strong>.
          </p>
        </div>
        <button
          onClick={() => window.print()}
          className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-2"
          data-testid="poster-print-btn"
        >
          Print poster
        </button>
      </div>

      {/* A4 canvas — 210mm x 297mm */}
      <div
        className="print-canvas mx-auto bg-white shadow-[0_8px_40px_rgba(0,0,0,0.12)] relative overflow-hidden"
        style={{ width: "210mm", minHeight: "297mm" }}
        data-testid="poster-canvas"
      >
        {/* Top dark band — brand strip */}
        <div className="bg-[#111] text-white px-10 py-7 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Compass className="w-7 h-7 text-[#ff4500]" strokeWidth={1.6} />
            <div>
              <div className="font-display text-3xl tracking-tight leading-none">CampCompass</div>
              <div className="mono text-[10px] uppercase tracking-[0.2em] text-white/70 mt-1">UK Edition</div>
            </div>
          </div>
          <div className="text-right">
            <div className="eyebrow text-[#ff4500]">est. 2026</div>
            <div className="mono text-[11px] text-white/80 mt-0.5">{PUBLIC_URL.replace(/^https?:\/\//, "")}</div>
          </div>
        </div>

        {/* Hero photo banner — UK landscape: tents, campfire, map & compass.
            Acts as the visual draw, then white text section underneath. */}
        <div className="relative h-[110mm] overflow-hidden bg-[#0d1f24]">
          <img
            src="/brand/poster-hero-v2.jpg"
            alt="UK camping landscape — tent beside a glassy mountain lake at golden hour"
            className="w-full h-full object-cover"
          />
          {/* Bottom-up gradient so the orange tagline sits crisp on it */}
          <div className="absolute inset-x-0 bottom-0 h-[55%] bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 px-10 pb-6 text-white">
            <div className="inline-flex items-center gap-2 mb-2 px-2.5 py-1 bg-[#ff4500]">
              <Heart className="w-3 h-3" strokeWidth={2.4} />
              <span className="mono text-[10px] uppercase tracking-[0.18em] font-bold">A camper community · Everyone welcome</span>
            </div>
            <h2 className="font-display tracking-tight leading-[0.95] text-[54px] drop-shadow-[0_2px_18px_rgba(0,0,0,0.7)]">
              Camping is for<br />
              <span className="text-[#ff8552]">all of us.</span>
            </h2>
          </div>
        </div>

        {/* Sub-claim */}
        <div className="px-10 pt-7 pb-7">
          <p className="text-[15px] text-[#333] leading-relaxed max-w-[600px]">
            <strong className="text-[#111]">Not another booking engine.</strong> CampCompass is the
            UK&apos;s first honest, community-built campsite directory — for first-timers, lifelong
            tourers, families with toddlers, retirees with caravans, solo wild-pitchers, dog
            people, festival folk and everyone in between. Real campers, real advice, no
            sponsored rankings.
          </p>
        </div>

        {/* Three big stats */}
        <div className="px-10 grid grid-cols-3 gap-3 mb-6">
          <Stat k={countLabel} v="UK campsites" />
          <Stat k="£1.99" v="Pro · per month" />
          <Stat k="14 days" v="Free trial" />
        </div>

        {/* LAUNCH DATE banner — the crucial "you can install now" message */}
        <div className="mx-10 mb-7 bg-[#ff4500] text-white px-5 py-4">
          <div className="flex items-start gap-3">
            <Calendar className="w-6 h-6 flex-shrink-0 mt-0.5" strokeWidth={2.2} />
            <div>
              <div className="font-display text-[20px] leading-tight">
                Going live <span className="underline decoration-2 underline-offset-2">{LAUNCH_LABEL}</span>
              </div>
              <div className="text-[12px] leading-snug mt-1 text-white/95">
                Install on your phone now, create your account, and you're ready the second we launch — no queue, no waiting. Early campers get founding-member perks.
              </div>
            </div>
          </div>
        </div>

        {/* Feature row — 4 icons */}
        <div className="px-10 grid grid-cols-2 gap-x-6 gap-y-5 mb-9">
          <Feat icon={Users} title="Kind, moderated community" body="Every age, every kit. No gatekeeping. The Campfire Code keeps it warm." />
          <Feat icon={MapPin} title="Compare side-by-side" body={`${countFeat} · price, distance, facilities, dog rules — all at once.`} />
          <Feat icon={ShieldCheck} title="Crowd-verified prices" body="When prices change, campers flag them. Real numbers, not guesses." />
          <Feat icon={Sparkles} title="AI Camper, free for all" body="3 free chats a day · packing, weather, pitch picks. No login required." />
        </div>

        {/* Big QR + scan steps — the conversion driver */}
        <div className="mx-10 mb-6 border-2 border-[#111] bg-[#111] text-white">
          <div className="grid grid-cols-[180px_1fr] gap-0 items-stretch">
            <div className="bg-white p-4 flex items-center justify-center">
              <QRCodeSVG
                value={QR_URL}
                size={150}
                level="M"
                bgColor="#ffffff"
                fgColor="#111111"
                aria-label={`QR code linking to ${PUBLIC_URL}`}
              />
            </div>
            <div className="p-5 flex flex-col justify-center">
              <div className="eyebrow text-[#ff4500] mb-1.5">Install in 30 seconds · no app store needed</div>
              <h3 className="font-display text-[22px] leading-tight mb-3">Get it on your home screen</h3>
              <ol className="space-y-1.5 text-[12px] text-white/90 leading-snug">
                <li><strong className="text-[#ff4500] mono mr-1.5">1.</strong>Open your phone camera & point at the square</li>
                <li><strong className="text-[#ff4500] mono mr-1.5">2.</strong>Tap the link that pops up</li>
                <li><strong className="text-[#ff4500] mono mr-1.5">3.</strong>Tap <strong>"Add to Home Screen"</strong> (iPhone) or <strong>"Install app"</strong> (Android)</li>
                <li><strong className="text-[#ff4500] mono mr-1.5">4.</strong>Open it · tap <strong>"Sign up"</strong> · done</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Device-specific quick reference — removes any "how do I do that?" friction */}
        <div className="mx-10 mb-7 grid grid-cols-2 gap-3 text-[10.5px]">
          <div className="border hairline p-3 bg-[#fafafa]">
            <div className="flex items-center gap-1.5 eyebrow text-[#111] mb-1">
              <Apple className="w-3 h-3" strokeWidth={2} /> iPhone / iPad
            </div>
            <div className="text-[#555] leading-snug">
              Open in <strong>Safari</strong> → tap the <strong>Share</strong> icon (square with up-arrow) → scroll down → <strong>"Add to Home Screen"</strong> → tap Add.
            </div>
          </div>
          <div className="border hairline p-3 bg-[#fafafa]">
            <div className="flex items-center gap-1.5 eyebrow text-[#111] mb-1">
              <Smartphone className="w-3 h-3" strokeWidth={2} /> Android
            </div>
            <div className="text-[#555] leading-snug">
              Open in <strong>Chrome</strong> → tap the <strong>⋮</strong> menu (top-right) → <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong> → Install.
            </div>
          </div>
        </div>

        {/* Talking-points strip — get them sharing */}
        <div className="mx-10 mb-7 border hairline p-5 bg-[#fafafa]">
          <div className="eyebrow text-[#ff4500] mb-2 flex items-center gap-1.5">
            <Heart className="w-3 h-3" /> Loved it? Tell another camper
          </div>
          <p className="text-[12.5px] leading-relaxed text-[#333]">
            Tag <strong className="mono text-[#111]">{SOCIAL.instagram.handle}</strong> on Instagram or share your trip on the
            in-app <strong>Trip Wall</strong>. We re-post our favourites (with credit) — and every honest review helps the
            next camper pick the right pitch.
          </p>
        </div>

        {/* Bottom strip — handles & URL */}
        <div className="absolute inset-x-0 bottom-0 px-10 py-5 bg-[#111] text-white flex items-center justify-between">
          <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-[0.18em]">
            <Tent className="w-3.5 h-3.5 text-[#ff4500]" strokeWidth={1.6} />
            Be kind · share knowledge · lift each other up
          </div>
          <div className="text-right mono text-[11px] leading-tight">
            <div className="text-white">{PUBLIC_URL.replace(/^https?:\/\//, "")}</div>
            <div className="text-white/55">{SOCIAL.instagram.handle} · {SOCIAL.hashtag}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ k, v }) => (
  <div className="border-l-4 border-[#ff4500] pl-3">
    <div className="font-display text-3xl leading-none">{k}</div>
    <div className="mono text-[9px] uppercase tracking-[0.12em] text-[#666] mt-1.5">{v}</div>
  </div>
);

const Feat = ({ icon: Icon, title, body }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-5 h-5 text-[#ff4500] flex-shrink-0 mt-0.5" strokeWidth={1.7} />
    <div>
      <div className="font-display text-[15px] leading-tight">{title}</div>
      <div className="text-[11.5px] text-[#555] mt-0.5 leading-snug">{body}</div>
    </div>
  </div>
);
